import { useState, useRef, useEffect } from "react";
import { AudioRecorder } from "./AudioRecorder";
import { Button } from "@/components/ui/button";
import { Headphones, Play, Pause, RotateCcw, CheckCircle2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

type TestMode = "copycat" | "play-along";

interface AudioTestStepProps {
  testKey: string;
  title: string;
  prompt: string;
  referenceTrackUrl?: string;
  maxDuration?: number;
  mode?: TestMode;
  onComplete: (testKey: string, blob: Blob, duration: number) => void;
  isCompleted: boolean;
}

export const AudioTestStep = ({
  testKey,
  title,
  prompt,
  referenceTrackUrl,
  maxDuration = 45,
  mode = "copycat",
  onComplete,
  isCompleted,
}: AudioTestStepProps) => {
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [listenPhaseComplete, setListenPhaseComplete] = useState(false);
  const [headphonesConfirmed, setHeadphonesConfirmed] = useState(false);
  const [isPlayingRef, setIsPlayingRef] = useState(false);
  const [refReady, setRefReady] = useState(!referenceTrackUrl);
  const refAudioRef = useRef<HTMLAudioElement | null>(null);

  // Load reference audio
  useEffect(() => {
    if (referenceTrackUrl) {
      const audio = new Audio(referenceTrackUrl);
      audio.preload = "auto";
      audio.addEventListener("canplaythrough", () => setRefReady(true));
      audio.addEventListener("ended", () => setIsPlayingRef(false));
      refAudioRef.current = audio;
      return () => { audio.pause(); audio.src = ""; };
    }
  }, [referenceTrackUrl]);

  const toggleReference = () => {
    if (!refAudioRef.current) return;
    if (isPlayingRef) {
      refAudioRef.current.pause();
      setIsPlayingRef(false);
    } else {
      refAudioRef.current.currentTime = 0;
      refAudioRef.current.play().catch(() => {});
      setIsPlayingRef(true);
    }
  };

  const handleListenComplete = () => {
    if (refAudioRef.current) {
      refAudioRef.current.pause();
      setIsPlayingRef(false);
    }
    setListenPhaseComplete(true);
  };

  const handleRecordingComplete = (blob: Blob, duration: number) => {
    setRecordingBlob(blob);
    onComplete(testKey, blob, duration);
  };

  const handleReRecord = () => {
    setRecordingBlob(null);
    if (isCopyCat) {
      setListenPhaseComplete(false);
    }
  };

  const isCopyCat = mode === "copycat";
  const readyToRecord = isCopyCat ? listenPhaseComplete : headphonesConfirmed;

  return (
    <div className="space-y-6">
      {/* Title & prompt */}
      <div className="text-center space-y-3">
        <h3 className="text-xl font-bold">{title}</h3>
        <div className="max-w-lg mx-auto bg-muted/50 rounded-lg p-4 border">
          <p className="text-sm text-foreground whitespace-pre-line">{prompt}</p>
        </div>
      </div>

      {/* COPY-CAT MODE: Listen phase */}
      {isCopyCat && !listenPhaseComplete && !isCompleted && (
        <div className="space-y-4 p-4 rounded-xl border bg-card">
          <p className="text-sm font-medium text-center">Step 1: Listen to the reference</p>
          <div className="flex justify-center gap-3">
            <Button
              onClick={toggleReference}
              disabled={!refReady}
              variant="outline"
              size="lg"
              className="gap-2"
            >
              {isPlayingRef ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              {!refReady ? "Loading..." : isPlayingRef ? "Pause" : "Play Reference"}
            </Button>
            {!isPlayingRef && (
              <Button
                onClick={() => {
                  if (refAudioRef.current) {
                    refAudioRef.current.currentTime = 0;
                  }
                }}
                variant="ghost"
                size="lg"
                disabled={!refReady}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Button
            onClick={handleListenComplete}
            className="w-full"
            disabled={!refReady}
          >
            I'm ready to record from memory →
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Listen as many times as you need. The track will NOT play during recording.
          </p>
        </div>
      )}

      {/* PLAY-ALONG MODE: Headphone gate */}
      {!isCopyCat && !headphonesConfirmed && !isCompleted && (
        <div className="space-y-4 p-4 rounded-xl border bg-card">
          <div className="flex items-center justify-center gap-3 text-primary">
            <Headphones className="h-8 w-8" />
            <p className="text-lg font-semibold">Headphones Required</p>
          </div>
          <p className="text-sm text-muted-foreground text-center max-w-md mx-auto">
            The reference track will play in your headphones during recording. 
            This ensures your microphone only captures your instrument — not the track.
          </p>
          <div className="flex items-center justify-center gap-3 p-3 rounded-lg bg-muted/50 border">
            <Checkbox
              id="headphones-check"
              checked={headphonesConfirmed}
              onCheckedChange={(checked) => setHeadphonesConfirmed(checked === true)}
            />
            <label htmlFor="headphones-check" className="text-sm font-medium cursor-pointer">
              I have headphones plugged in and ready
            </label>
          </div>
        </div>
      )}

      {/* Recording phase */}
      {readyToRecord && !isCompleted && (
        <div className="space-y-2">
          {isCopyCat && (
            <p className="text-sm font-medium text-center text-primary">
              Step 2: Record your take from memory
            </p>
          )}
          <AudioRecorder
            referenceTrackUrl={isCopyCat ? undefined : referenceTrackUrl}
            maxDurationSeconds={maxDuration}
            onRecordingComplete={handleRecordingComplete}
            onReRecord={handleReRecord}
            disabled={isCompleted}
          />
        </div>
      )}

      {isCompleted && (
        <div className="text-center flex items-center justify-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-primary" />
          <p className="text-sm text-primary font-medium">Test completed</p>
        </div>
      )}
    </div>
  );
};
